'use client';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsMobile } from '@/lib/hooks/useIsMobile';
import { DateTimePicker } from '@/lib/ui-kit/date-time-picker';
import { Icon } from '@/lib/ui-kit/icons';
import { Popover, PopoverContent, PopoverTrigger } from '@/lib/ui-kit/popover';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';

dayjs.extend(utc);

const selectItemClassName = `flex w-full cursor-pointer select-none items-center rounded-[8px] px-3 py-2 text-left
              text-[13px] font-normal leading-4 tracking-[-0.13px] text-v2-text-primary transition-colors
              hover:bg-v2-btn-secondary-hover focus-visible:bg-v2-btn-secondary-hover outline-none`;

// End of day in UTC
const getEndOfDay = () => {
  return dayjs().utc().endOf('day').toDate();
};

// Format date as "24-APR-2025, 15:45 UTC"
const formatDateDisplay = (date: Date): string => {
  const expireDate = dayjs(date).utc();
  const day = expireDate.date();
  const month = expireDate.format('MMM').toUpperCase();
  const year = expireDate.year();
  const hours = String(expireDate.hour()).padStart(2, '0');
  const minutes = String(expireDate.minute()).padStart(2, '0');
  return `${day}-${month}-${year}, ${hours}:${minutes} UTC`;
};

type ExpirationDateSelectProps = {
  value?: Date;
  onChange?: (value?: Date) => void;
};

export const ExpirationDateSelect = ({ value, onChange }: ExpirationDateSelectProps) => {
  const t = useTranslations('expiration');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const isMobile = useIsMobile();

  const [isDateTimeValid, setIsDateTimeValid] = useState(true);

  const nowDate = useMemo(() => {
    return new Date();
  }, []);

  const [tempDate, setTempDate] = useState<Date | undefined>(undefined);
  const [date, setDate] = useState<Date | undefined>(value);

  useEffect(() => {
    setDate(value);
  }, [value]);

  const isEndOfDaySelected = useMemo(() => {
    return dayjs(date).isSame(getEndOfDay(), 'hour');
  }, [date]);

  const updateDate = useCallback(
    (newDate?: Date) => {
      setDate(newDate);
      onChange?.(newDate);
    },
    [onChange],
  );

  useEffect(() => {
    if (!date) {
      updateDate(getEndOfDay());
    }
  }, [date, updateDate]);

  const handleSelectEndOfDay = useCallback(() => {
    updateDate(getEndOfDay());
    setPopoverOpen(false);
    setShowCalendar(false);
  }, [updateDate]);

  const handleSelectCustom = useCallback(() => {
    setTempDate(isEndOfDaySelected ? dayjs().utc().toDate() : date);
    setShowCalendar(true);
  }, [date, isEndOfDaySelected]);

  const handleDateChange = useCallback((newDate?: Date) => {
    setTempDate(newDate);
  }, []);

  const handleCancel = useCallback(() => {
    setPopoverOpen(false);
  }, []);

  const handleSet = useCallback(() => {
    if (tempDate && isDateTimeValid) {
      updateDate(tempDate);
      setPopoverOpen(false);
    }
  }, [tempDate, isDateTimeValid, updateDate]);

  return (
    <Popover
      open={popoverOpen}
      onOpenChange={(open) => {
        if (open) {
          setShowCalendar(false);
        }
        setPopoverOpen(open);
      }}
    >
      <PopoverTrigger
        className={cn(
          `flex h-11 w-full cursor-pointer items-center justify-between rounded-[10px] bg-v2-toggle-neutral-surface
          px-4 py-3 transition-colors`,
        )}
      >
        {date ? (
          <>
            <span className="text-[13px] leading-[1.5] text-v2-text-primary">
              {isEndOfDaySelected ? t('endOfDay') : formatDateDisplay(date)}
            </span>
            <Icon
              name="ArrowSmallDown"
              size={20}
              className={cn(
                'shrink-0 text-v2-text-secondary opacity-50 transition-transform duration-200',
                popoverOpen && 'rotate-180',
              )}
            />
          </>
        ) : (
          <div />
        )}
      </PopoverTrigger>
      <PopoverContent
        side={showCalendar ? (isMobile ? 'bottom' : 'left') : 'bottom'}
        align={showCalendar ? (isMobile ? 'center' : 'end') : 'start'}
        alignOffset={showCalendar ? (isMobile ? 0 : -100) : 0}
        sideOffset={showCalendar ? (isMobile ? 8 : 20) : 4}
        className={cn(
          'border-none shadow-[1px_1px_8px_0px_rgba(0,0,0,0.2)]',
          showCalendar
            ? 'w-[320px] rounded-[10px] bg-v2-surface-primary px-6 pt-6 pb-4'
            : 'w-[var(--radix-popover-trigger-width)] rounded-[10px] bg-v2-surface-secondary p-1',
        )}
      >
        {showCalendar ? (
          <div className="flex flex-col gap-4">
            <DateTimePicker
              value={tempDate}
              onChange={handleDateChange}
              minDate={nowDate}
              onValidityChange={setIsDateTimeValid}
            />
            <div className="flex gap-2">
              <Button appearance="neutral" size="M" onClick={handleCancel} className="h-10 flex-1">
                {t('cancel')}
              </Button>
              <Button
                appearance="accent"
                size="M"
                onClick={handleSet}
                disabled={!isDateTimeValid || !tempDate}
                className="h-10 flex-1"
              >
                {t('set')}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <button
              onClick={handleSelectEndOfDay}
              className={cn(
                selectItemClassName,
                isEndOfDaySelected && 'bg-v2-bg-blue-10 text-v2-bg-blue',
              )}
            >
              {t('endOfDay')}
            </button>
            <button
              onClick={handleSelectCustom}
              className={cn(
                selectItemClassName,
                !isEndOfDaySelected && 'bg-v2-bg-blue-10 text-v2-bg-blue',
              )}
            >
              {t('custom')}
            </button>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
